"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { TriangleAlert, ShieldAlert, Zap, Activity, BellRing, Lock, Layers3 } from "lucide-react";
const logicSteps = [{
  id: "step-80",
  threshold: "80%",
  title: "Automated warning",
  description: "System alerts administrators when capacity reaches 80%, giving time to review usage before service is affected.",
  icon: BellRing,
  colorClass: "text-amber-500",
  bgClass: "bg-amber-500/10"
}, {
  id: "step-100",
  threshold: "100%",
  title: "Hard cap enforced",
  description: "Once the limit is reached, additional usage is blocked. No hidden overages and no fallback platform keys are used.",
  icon: Lock,
  colorClass: "text-destructive",
  bgClass: "bg-destructive/10"
}, {
  id: "step-resolve",
  threshold: "Next step",
  title: "Add capacity or upgrade",
  description: "Resume growth by adding a Capacity Pack for the constrained resource or moving to a higher subscription tier.",
  icon: Zap,
  colorClass: "text-primary",
  bgClass: "bg-primary/10"
}];
export default function UsageTransparency() {
  return <section id="usage-transparency" className="bg-background py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-x-16 gap-y-16 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-sm font-medium text-muted-foreground mb-6">
              <Activity className="h-4 w-4 text-primary" />
              <span>Usage Transparency</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Hard caps make scaling behavior clear
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              No surprise bills. No hidden overages. EloRAG uses backend-enforced hard caps so teams
              always know what happens as usage grows under the BYO-Key model.
            </p>

            <div className="mt-10 space-y-8">
              {logicSteps.map((step, i) => {
              const Icon = step.icon;
              return <div key={step.id} data-index={i} className="flex gap-4">
                    <div className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${step.bgClass}`}>
                      <Icon className={`h-5 w-5 ${step.colorClass}`} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                        <span className="inline-flex items-center justify-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {step.threshold}
                        </span>
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="mt-10 flex items-center gap-4">
              <Button asChild size="lg">
                <Link to="/CapacityPacksPage">
                  View Capacity Packs
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/PricingPage">
                  See Pricing Plans
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-3xl bg-primary/5 blur-2xl" aria-hidden="true" />

            <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-xl">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
                    <ShieldAlert className="h-3 w-3 text-primary-foreground" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">EloRAG Admin</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  <Layers3 className="size-3.5" />
                  Controlled limits
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-foreground">Workspace Usage</h4>
                  <p className="text-sm text-muted-foreground">Current billing cycle</p>
                </div>

                <div className="rounded-lg border border-border bg-background p-5 shadow-sm">
                  <div className="mb-4 flex items-end justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">AI Queries</p>
                      <p className="text-2xl font-bold text-foreground mt-1">
                        85,000 <span className="text-sm font-normal text-muted-foreground">/ 100,000</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-500">
                        85% Used
                      </span>
                    </div>
                  </div>

                  <Progress value={85} className="h-2.5 w-full [&>div]:bg-amber-500" />

                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Reset in 12 days</span>
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-500">
                      <TriangleAlert className="h-3 w-3" />
                      Approaching limit
                    </span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 opacity-60 grayscale transition-all hover:grayscale-0 hover:opacity-100">
                  <div className="rounded-lg border border-border bg-background p-4">
                    <p className="text-xs font-medium text-muted-foreground">Storage</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">42 GB</p>
                    <Progress value={42} className="mt-3 h-1.5 w-full" />
                  </div>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <p className="text-xs font-medium text-muted-foreground">Vectors</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">1.8M / 2.5M</p>
                    <Progress value={72} className="mt-3 h-1.5 w-full" />
                  </div>
                </div>
              </div>

              <div className="absolute bottom-6 right-6 z-20 flex w-[calc(100%-3rem)] sm:w-80 animate-in slide-in-from-bottom-8 fade-in duration-500 flex-col gap-1 rounded-lg border border-border bg-popover p-4 shadow-lg">
                <div className="flex items-start gap-3">
                  <TriangleAlert className="mt-0.5 h-5 w-5 text-amber-500 shrink-0" />
                  <div className="grid gap-1">
                    <p className="text-sm font-semibold text-popover-foreground">Alert: Approaching Limit</p>
                    <p className="text-sm text-muted-foreground">Workspace has reached 85% of monthly query capacity. Service will pause at 100% unless capacity is added.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}